
/**
 * LESSON PROGRESS TRACKING AUDIT - 2025-10-24
 *
 * Total lesson wrappers: 39 routes
 * Unique lesson IDs: 21 (some multi-step workflows share IDs)
 *
 * Breakdown by chapter:
 *   - Chapter 0: 7 lessons (12 routes total including legacy)
 *   - Chapter 1: Hub only (no direct lesson routes)
 *   - Chapter 2: 4 lessons (all wrapped)
 *   - Chapter 3: 3 workshops (all wrapped)
 *   - Campaign Planning Workshop: 7 steps (shared ID)
 *   - Mission Align Simulator: 7 steps (shared ID)
 *   - Standalone Workshops: 5 (AI Brief Builder, Strategic Comm, Content Intelligence, Quality Spotting, AI Comms Hub)
 *
 * Lesson types:
 *   - Workshops: 18 routes
 *   - Simulators: 2 routes
 *   - Lessons: 19 routes
 *   - Landing pages: NOT wrapped (navigation only)
 *   - Hub pages: NOT wrapped (chapter navigation)
 *
 * Coverage: 100% of interactive lesson/workshop routes wrapped
 *
 * NOTE: Legacy chapter and workshop routes are archived and redirect to /micro-dashboard.
 * All multi-step workflows correctly use shared lessonIds for continuity
 */

import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider } from "@/contexts/AuthContext";
import { CharacterStoryProvider } from "@/contexts/CharacterStoryContext";
import { GlobalChatProvider } from "@/contexts/GlobalChatContext";
import { VoiceAndToneProvider } from "@/contexts/VoiceAndToneContext";
import { MayaCharacterProvider } from "@/contexts/MayaCharacterProvider";
import { SafetyModeProvider } from "@/contexts/SafetyModeContext";
import { TestingModeProvider } from "@/contexts/TestingModeContext";
import { TestingModeIndicator } from "@/components/testing/TestingModeIndicator";
import { TestingFloatingNav } from "@/components/testing/TestingFloatingNav";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import { PerformanceWrapper, PerformanceMonitor, BundleAnalyzer, AccessibilityTester } from "@/components/performance/PerformanceIntegration";
import { BrandedToastContainer } from "@/hooks/use-branded-toast";
import Index from "./pages/Index";
import Auth from "./pages/Auth";
import MicroDashboardPage from "./pages/MicroDashboard";
import MicroLessonPage from "./pages/MicroLessonPage";
import Profile from "./pages/Profile";
import CompleteProfile from "./pages/CompleteProfile";
import NotFound from "./pages/NotFound";
import AdminProfileBackfill from "./pages/AdminProfileBackfill";
import AdminFixProgress from "./pages/AdminFixProgress";
import { Admin } from "./pages/Admin";
import { AdminPrompts } from "./pages/AdminPrompts";
import TestLyra from "./pages/TestLyra";
import TestMaya from "./pages/TestMaya";
import MockLanding from "./pages/MockLanding";
import MockChapter from "./pages/MockChapter";
import IconsHub from "./pages/IconsHub";
import MobileUXDemo from "./components/mobile-ui/MobileUXDemo";
import DemoExperience from "./pages/DemoExperience";
import DemoOption2 from "./pages/DemoOption2";
import DemoOption3 from "./pages/DemoOption3";
import TestQuickReplies from "./pages/TestQuickReplies";
import Redeem from "./pages/Redeem";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import TermsOfService from "./pages/TermsOfService";

import RouteChangeHandler from "@/components/navigation/RouteChangeHandler";
import NewOnboardingFlow from "./components/onboarding/NewOnboardingFlow";
import PathMapPage from "./pages/PathMap";

const queryClient = new QueryClient();

const App = () => {
  return (
    <PerformanceWrapper
      enablePerformanceMonitoring={true}
      enableAccessibilityFeatures={true}
    >
      <QueryClientProvider client={queryClient}>
        <TooltipProvider>
          <Toaster />
          <Sonner />
          <AuthProvider>
            <CharacterStoryProvider>
              <VoiceAndToneProvider>
                <MayaCharacterProvider>
                  <SafetyModeProvider>
                    <TestingModeProvider>
                      <BrowserRouter>
                        <RouteChangeHandler />
                        <GlobalChatProvider>
                          <Routes>
                            <Route path="/" element={<Index />} />
                            <Route path="/redeem" element={<Redeem />} />
                            <Route path="/onboarding" element={<NewOnboardingFlow />} />
                            <Route path="/map" element={<PathMapPage />} />
                            {/* Primary redirect for authenticated users to dashboard */}
                            <Route path="/journey" element={<Navigate to="/micro-dashboard" replace />} />
                            <Route path="/mock" element={<MockLanding />} />
                            <Route path="/mock-chapter" element={<MockChapter />} />
                            <Route path="/auth" element={<Auth />} />
                            <Route path="/privacy" element={<PrivacyPolicy />} />
                            <Route path="/terms" element={<TermsOfService />} />
                            <Route path="/admin" element={<Admin />} />
                            <Route path="/test-lyra" element={<TestLyra />} />
                            <Route path="/test-maya" element={<TestMaya />} />
                            <Route path="/admin/profile-backfill" element={<AdminProfileBackfill />} />
                            <Route path="/admin/fix-progress" element={<AdminFixProgress />} />
                            <Route path="/admin/prompts" element={<AdminPrompts />} />
                            <Route
                              path="/dashboard"
                              element={<Navigate to="/micro-dashboard" replace />}
                            />
                            <Route
                              path="/micro-dashboard"
                              element={
                                <ProtectedRoute>
                                  <MicroDashboardPage />
                                </ProtectedRoute>
                              }
                            />
                            <Route
                              path="/legacy-dashboard"
                              element={<Navigate to="/micro-dashboard" replace />}
                            />
                            <Route
                              path="/dashboard-old"
                              element={<Navigate to="/micro-dashboard" replace />}
                            />
                            <Route
                              path="/profile"
                              element={
                                <ProtectedRoute>
                                  <Profile />
                                </ProtectedRoute>
                              }
                            />
                            <Route
                              path="/complete-profile"
                              element={
                                <ProtectedRoute>
                                  <CompleteProfile />
                                </ProtectedRoute>
                              }
                            />
                            <Route
                              path="/micro/grant-negotiation"
                              element={
                                <ProtectedRoute>
                                  <MicroLessonPage lessonId="grant-negotiation" />
                                </ProtectedRoute>
                              }
                            />
                            <Route
                              path="/micro/voice-tune-up"
                              element={
                                <ProtectedRoute>
                                  <MicroLessonPage lessonId="voice-tune-up" />
                                </ProtectedRoute>
                              }
                            />
                            <Route
                              path="/micro/lyra-daily-pulse"
                              element={
                                <ProtectedRoute>
                                  <MicroLessonPage lessonId="daily-pulse" />
                                </ProtectedRoute>
                              }
                            />
                            <Route
                              path="/micro/prompt-sprint"
                              element={
                                <ProtectedRoute>
                                  <MicroLessonPage lessonId="prompt-sprint" />
                                </ProtectedRoute>
                              }
                            />
                            <Route
                              path="/micro/automation-spotlight"
                              element={
                                <ProtectedRoute>
                                  <MicroLessonPage lessonId="automation-spotlight" />
                                </ProtectedRoute>
                              }
                            />
                            <Route
                              path="/micro/newsletter-remix"
                              element={
                                <ProtectedRoute>
                                  <MicroLessonPage lessonId="newsletter-remix" />
                                </ProtectedRoute>
                              }
                            />
                            <Route
                              path="/micro/signal-scan"
                              element={
                                <ProtectedRoute>
                                  <MicroLessonPage lessonId="signal-scan" />
                                </ProtectedRoute>
                              }
                            />
                            <Route
                              path="/micro/ai-inbox-triage"
                              element={
                                <ProtectedRoute>
                                  <MicroLessonPage lessonId="inbox-triage" />
                                </ProtectedRoute>
                              }
                            />
                            <Route
                              path="/micro/crisis-triage"
                              element={
                                <ProtectedRoute>
                                  <MicroLessonPage lessonId="crisis-triage" />
                                </ProtectedRoute>
                              }
                            />
                            <Route
                              path="/micro/ops-playbook-builder"
                              element={
                                <ProtectedRoute>
                                  <MicroLessonPage lessonId="ops-playbook-builder" />
                                </ProtectedRoute>
                              }
                            />
                            <Route
                              path="/micro/spot-hallucinations"
                              element={
                                <ProtectedRoute>
                                  <MicroLessonPage lessonId="spot-hallucinations" />
                                </ProtectedRoute>
                              }
                            />
                            <Route
                              path="/micro/impact-reflection"
                              element={
                                <ProtectedRoute>
                                  <MicroLessonPage lessonId="impact-reflection" />
                                </ProtectedRoute>
                              }
                            />
                            <Route
                              path="/micro/focus-reset"
                              element={
                                <ProtectedRoute>
                                  <MicroLessonPage lessonId="focus-reset" />
                                </ProtectedRoute>
                              }
                            />
                            <Route
                              path="/micro/research-brief-lab"
                              element={
                                <ProtectedRoute>
                                  <MicroLessonPage lessonId="research-brief-lab" />
                                </ProtectedRoute>
                              }
                            />
                            <Route
                              path="/micro/momentum-brief"
                              element={
                                <ProtectedRoute>
                                  <MicroLessonPage lessonId="momentum-brief" />
                                </ProtectedRoute>
                              }
                            />

                            {/* Chapter 2 & 3 Micro Lessons - Use generic MicroLessonPage with dynamic ID */}
                            <Route
                              path="/micro/quality-control"
                              element={
                                <ProtectedRoute>
                                  <MicroLessonPage />
                                </ProtectedRoute>
                              }
                            />
                            <Route
                              path="/micro/ai-briefing"
                              element={
                                <ProtectedRoute>
                                  <MicroLessonPage />
                                </ProtectedRoute>
                              }
                            />
                            <Route
                              path="/micro/content-intelligence"
                              element={
                                <ProtectedRoute>
                                  <MicroLessonPage />
                                </ProtectedRoute>
                              }
                            />
                            <Route
                              path="/micro/strategic-direction"
                              element={
                                <ProtectedRoute>
                                  <MicroLessonPage />
                                </ProtectedRoute>
                              }
                            />
                            <Route
                              path="/micro/communication-systems"
                              element={
                                <ProtectedRoute>
                                  <MicroLessonPage />
                                </ProtectedRoute>
                              }
                            />
                            <Route
                              path="/micro/analyze-options"
                              element={
                                <ProtectedRoute>
                                  <MicroLessonPage />
                                </ProtectedRoute>
                              }
                            />
                            <Route
                              path="/micro/build-plans"
                              element={
                                <ProtectedRoute>
                                  <MicroLessonPage />
                                </ProtectedRoute>
                              }
                            />
                            <Route
                              path="/micro/monitor-finances"
                              element={
                                <ProtectedRoute>
                                  <MicroLessonPage />
                                </ProtectedRoute>
                              }
                            />
                            <Route
                              path="/micro/optimize-workflows"
                              element={
                                <ProtectedRoute>
                                  <MicroLessonPage />
                                </ProtectedRoute>
                              }
                            />
                            <Route
                              path="/micro/automated-oversight"
                              element={
                                <ProtectedRoute>
                                  <MicroLessonPage />
                                </ProtectedRoute>
                              }
                            />

                            {/* AI Foundations Micro Lessons (14 lessons across 5 topics) */}
                            <Route path="/micro/ai-foundations-be-specific" element={<ProtectedRoute><MicroLessonPage /></ProtectedRoute>} />
                            <Route path="/micro/ai-foundations-provide-context" element={<ProtectedRoute><MicroLessonPage /></ProtectedRoute>} />
                            <Route path="/micro/ai-foundations-set-format" element={<ProtectedRoute><MicroLessonPage /></ProtectedRoute>} />
                            <Route path="/micro/ai-foundations-include-examples" element={<ProtectedRoute><MicroLessonPage /></ProtectedRoute>} />
                            <Route path="/micro/ai-foundations-raise-framework" element={<ProtectedRoute><MicroLessonPage /></ProtectedRoute>} />
                            <Route path="/micro/ai-foundations-understand-models" element={<ProtectedRoute><MicroLessonPage /></ProtectedRoute>} />
                            <Route path="/micro/ai-foundations-text-image-tools" element={<ProtectedRoute><MicroLessonPage /></ProtectedRoute>} />
                            <Route path="/micro/ai-foundations-ai-limitations" element={<ProtectedRoute><MicroLessonPage /></ProtectedRoute>} />
                            <Route path="/micro/ai-foundations-tool-strategy" element={<ProtectedRoute><MicroLessonPage /></ProtectedRoute>} />
                            <Route path="/micro/ai-foundations-ethical-considerations" element={<ProtectedRoute><MicroLessonPage /></ProtectedRoute>} />
                            <Route path="/micro/ai-foundations-data-privacy" element={<ProtectedRoute><MicroLessonPage /></ProtectedRoute>} />
                            <Route path="/micro/ai-foundations-bias-fairness" element={<ProtectedRoute><MicroLessonPage /></ProtectedRoute>} />
                            <Route path="/micro/ai-foundations-ethical-guidelines" element={<ProtectedRoute><MicroLessonPage /></ProtectedRoute>} />
                            <Route path="/micro/ai-foundations-setup-toolkit" element={<ProtectedRoute><MicroLessonPage /></ProtectedRoute>} />

                            <Route
                              path="/content-lab"
                              element={<Navigate to="/micro-dashboard" replace />}
                            />
                            <Route
                              path="/prompt"
                              element={<Navigate to="/micro-dashboard" replace />}
                            />
                            {/* Chapter and Lesson Routes */}
                            <Route
                              path="/lesson/:lessonId"
                              element={<Navigate to="/micro-dashboard" replace />}
                            />
                            {/* Chapter routes - legacy chapter system archived */}
                            <Route path="/chapter/0/*" element={<Navigate to="/micro-dashboard" replace />} />
                            <Route path="/chapter-0/*" element={<Navigate to="/micro-dashboard" replace />} />
                            <Route path="/chapter/2/*" element={<Navigate to="/micro-dashboard" replace />} />
                            <Route path="/chapter/2-new/*" element={<Navigate to="/micro-dashboard" replace />} />
                            <Route path="/chapter/:chapterId/*" element={<Navigate to="/micro-dashboard" replace />} />
                            {/* Redirect legacy Maya demo route to micro dashboard */}
                            <Route
                              path="/lyra-maya-demo"
                              element={<Navigate to="/micro-dashboard" replace />}
                            />
                            {/* Test Quick Replies - Debug Route */}
                            <Route
                              path="/test-quick-replies"
                              element={
                                <ProtectedRoute>
                                  <TestQuickReplies />
                                </ProtectedRoute>
                              }
                            />
                            {/* Storage Routes */}
                            <Route
                              path="/storage"
                              element={<Navigate to="/micro-dashboard" replace />}
                            />
                            <Route
                              path="/storage/sofia"
                              element={<Navigate to="/micro-dashboard" replace />}
                            />
                            <Route
                              path="/icons"
                              element={
                                <ProtectedRoute>
                                  <IconsHub />
                                </ProtectedRoute>
                              }
                            />
                            <Route
                              path="/storage/sofia/:lessonId"
                              element={<Navigate to="/micro-dashboard" replace />}
                            />
                            {/* Mobile UX Demo Route */}
                            <Route
                              path="/mobile-ux-demo"
                              element={
                                <ProtectedRoute>
                                  <MobileUXDemo />
                                </ProtectedRoute>
                              }
                            />
                            {/* Clean Interactive AI Training at /options */}
                            <Route
                              path="/options"
                              element={<Navigate to="/micro-dashboard" replace />}
                            />
                            {/* Maya's Quality Spotting System */}
                            <Route
                              path="/quality-spotting"
                              element={<Navigate to="/micro-dashboard" replace />}
                            />
                            {/* Mission Alignment Simulator (archived) */}
                            <Route
                              path="/missionalign-simulator/*"
                              element={<Navigate to="/micro-dashboard" replace />}
                            />
                            {/* Legacy workshops (archived) */}
                            <Route
                              path="/ai-brief-builder/*"
                              element={<Navigate to="/micro-dashboard" replace />}
                            />
                            <Route
                              path="/content-intelligence-simulator"
                              element={<Navigate to="/micro-dashboard" replace />}
                            />
                            <Route
                              path="/strategic-communication"
                              element={<Navigate to="/micro-dashboard" replace />}
                            />
                            <Route
                              path="/strategic-communication-simulator/*"
                              element={<Navigate to="/micro-dashboard" replace />}
                            />
                            <Route
                              path="/ai-comms-hub"
                              element={<Navigate to="/micro-dashboard" replace />}
                            />
                            {/* Standalone Demo Experience */}
                            <Route
                              path="/demo"
                              element={<DemoExperience />}
                            />
                            {/* Demo Option 2: Choose Your Own Adventure */}
                            <Route
                              path="/demo-option-2"
                              element={<DemoOption2 />}
                            />
                            {/* Demo Option 3: Live Workshop */}
                            <Route
                              path="/demo-option-3"
                              element={<DemoOption3 />}
                            />
                            {/* Multi-Route Workshop Experience (archived) */}
                            <Route
                              path="/workshop/*"
                              element={<Navigate to="/micro-dashboard" replace />}
                            />

                            <Route path="*" element={<NotFound />} />
                          </Routes>
                          {/* Testing components disabled for production */}
                          {/* <TestingModeIndicator />
                <TestingFloatingNav /> */}
                        </GlobalChatProvider>
                      </BrowserRouter>
                    </TestingModeProvider>
                  </SafetyModeProvider>
                </MayaCharacterProvider>
              </VoiceAndToneProvider>
            </CharacterStoryProvider>
          </AuthProvider>

          {/* Development Tools */}
          <PerformanceMonitor />
          <BundleAnalyzer />
          <AccessibilityTester />

          {/* Branded Toast Container */}
          <BrandedToastContainer />
        </TooltipProvider>
      </QueryClientProvider>
    </PerformanceWrapper>
  );
};

export default App;
